import { WSForm, WSSelect } from "@wingspanhq/fe-component-library";
import { Engagement } from "@wingspanhq/wingspan-api-sdk";
import { useFormContext } from "react-hook-form";
import { useCreateEngagementModal } from "../../../EngagementTypes/modals/CreateEngagementModal";
import { EngagementOption } from "../../../EngagementTypes/selectors/selectorEngagementOptions";

export const FIELD_NAME_ENGAGEMENT_IDS = "engagementIds";

type Props = {
  options: EngagementOption[];
  /** Called with the engagement created from inside this field. */
  onEngagementCreated: (engagement: Engagement) => void;
};

/**
 * The engagement picker of the add-contractor form, plus the escape hatch out of
 * it: a payer with no engagement yet can create one without leaving the form.
 *
 * At least one engagement is required — v1 attached a new contractor to a hidden
 * default engagement, while v3 needs a real one to hang classification,
 * requirements, and payables on. So the empty menu offers a way out instead of
 * being a dead end, and the created engagement is selected immediately.
 *
 * Lives in its own component for `useFormContext`: selecting the new engagement
 * means writing to a field the form owns, from a callback the field itself
 * renders.
 */
export const FieldEngagementIds: React.FC<Props> = props => {
  const { getValues, setValue } = useFormContext();
  const modalCreateEngagement = useCreateEngagementModal();

  const createEngagement = async () => {
    const engagement = await modalCreateEngagement.open({});

    if (!engagement?.id) {
      return;
    }

    props.onEngagementCreated(engagement);

    const selectedIds: string[] =
      getValues(FIELD_NAME_ENGAGEMENT_IDS) ?? ([] as string[]);

    setValue(FIELD_NAME_ENGAGEMENT_IDS, [...selectedIds, engagement.id], {
      shouldValidate: true
    });
  };

  return (
    <WSForm.Field
      name={FIELD_NAME_ENGAGEMENT_IDS}
      label="Engagements *"
      component={WSSelect}
      componentProps={{
        mode: "multiple",
        placeholder: "Select engagements",
        tooltip: {
          dark: true,
          title: "Engagements (required)",
          children: `An engagement is a type of work arrangement that allows payers to organize their diverse work relationships in a flexible way. Every contractor is added under at least one engagement, and you can assign more than one.

If you don't have an engagement yet, create one right from this field. Engagements can be updated in the Contractors section of the app at any time.`
        },
        options: props.options,
        menuFooterText: "Don't see the engagement you need?",
        menuFooterAction: {
          label: "Create engagement",
          type: "button",
          onClick: createEngagement
        },
        emptyState: {
          type: "documents",
          title: "No engagements yet",
          description:
            "Create an engagement to add this contractor under — for example the project, role, or department they work on.",
          actions: {
            buttons: [
              {
                label: "Create engagement",
                kind: "Secondary",
                type: "button",
                onClick: createEngagement
              }
            ]
          }
        }
      }}
    />
  );
};
